import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import PageLayout from '@/components/Layout/PageLayout';
import { retryFailedQueries } from '@/components/retryFailedQueries';
import { usePermissions } from '@/contexts/auth';
import { useCage, useCageName, useCageTimezone } from '@/contexts/cage';
import useCageFilter from '@/contexts/cage-filter-context';
import { useGetFeedConversionRate } from '@/domain/biomass';
import { OVERVIEW_WEIGHT_UNIT_TAG, useGetCageOverview } from '@/domain/cages';
import { useIsSmolt } from '@/hooks/useCageTypeFlags';
import { useCageOverviewDateRange } from '@/routes/Client/Cage/components/cage-overview-context';
import { useHydrateOverviewDisplayDates } from '@/routes/Client/Cage/overviewCharts';
import type { CagePageInterface } from '@/routes/Client/Cage/utils/cage-pages-interface';
import filterGraphDayparts, {
    resolveWeightDaypartTag,
} from '@/routes/Client/Cage/utils/filter-graph-dayparts';

import CageWeightGraphsView from './CageWeightGraphsView';

const LIVING_WEIGHT_ALL_TAGS_FLAG = 'LIVING_WEIGHT_ALL_TAGS_ENABLED';

export default function CageWeightGraphsContainer({ cageId }: CagePageInterface) {
    const { t } = useTranslation();
    const { client } = useCage();
    const cageName = useCageName();
    const { weightUnitTag, daypartTag, selectedFishGroupId } = useCageFilter();
    const isSmolt = useIsSmolt();
    const resolvedDaypartTag = resolveWeightDaypartTag(isSmolt, daypartTag);
    const { isOptoScaleAdmin } = usePermissions();

    const { queryDateFrom, queryDateTo, dateFrom, dateTo, onDateChange, setDisplayDates } =
        useCageOverviewDateRange(cageId);

    const overviewParams = useMemo(
        () => ({
            from: queryDateFrom,
            to: queryDateTo,
            daypartTag: resolvedDaypartTag,
            // The cohort named in the title and breadcrumb (`fg`), as in `CageOverviewProvider`,
            // so this page and the provider share one query and answer for the same group.
            fishGroupId: selectedFishGroupId,
        }),
        [queryDateFrom, queryDateTo, resolvedDaypartTag, selectedFishGroupId]
    );

    const overviewQuery = useGetCageOverview(cageId, overviewParams);
    const { data, isLoading, error } = overviewQuery;

    useHydrateOverviewDisplayDates({
        lastDay: data.biomass.estimate.lastDay || undefined,
        isLoading,
        queryDateFrom,
        queryDateTo,
        timezone: useCageTimezone(),
        setDisplayDates,
    });

    const feedConversionRate = useGetFeedConversionRate(cageId, {
        from: dateFrom,
        to: dateTo,
        weightUnitTag,
    });

    /**
     * `LIVING_WEIGHT_ALL_TAGS_ENABLED` opts a client into seeing every daypart
     * series rather than only the default one. Set it on the client's feature
     * flags in the database to enable it.
     */
    const hasLivingWeightAllTagsEnabled =
        client?.featureFlags?.includes(LIVING_WEIGHT_ALL_TAGS_FLAG) ?? false;

    const graphedDaypartTags = useMemo(
        () => filterGraphDayparts(isSmolt, isOptoScaleAdmin, hasLivingWeightAllTagsEnabled),
        [isSmolt, isOptoScaleAdmin, hasLivingWeightAllTagsEnabled]
    );

    // Only the daypart selected in the page filter starts visible. Smolt cages
    // always use `all`, since they have no day/night split.
    const activeDaypartTags = useMemo(() => [resolvedDaypartTag], [resolvedDaypartTag]);

    return (
        <PageLayout title={cageName} subtitle={t('common.labels.graphs')} width="wide">
            <CageWeightGraphsView
                days={data.biomass.days}
                graphedDaypartTags={graphedDaypartTags}
                activeDaypartTags={activeDaypartTags}
                daypartTag={resolvedDaypartTag}
                weightUnitTag={OVERVIEW_WEIGHT_UNIT_TAG}
                isLoading={isLoading}
                error={error}
                retry={retryFailedQueries(overviewQuery)}
                dateFrom={dateFrom}
                dateTo={dateTo}
                onDateChange={onDateChange}
                feedConversionRateDays={feedConversionRate.data.days}
                isFeedConversionRateLoading={feedConversionRate.isLoading}
                feedConversionRateError={feedConversionRate.error}
                feedConversionRateRetry={retryFailedQueries(feedConversionRate)}
                isFeedConversionRateRefreshing={feedConversionRate.data.refreshing}
                isFeedConversionRateExhausted={feedConversionRate.pollingSlow}
                feedConversionRateLastFetchedAt={feedConversionRate.data.lastFetchedAt}
                isFeedConversionRateCageLevel={selectedFishGroupId != null}
            />
        </PageLayout>
    );
}
